import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Target, Zap, AlertCircle, X, Info } from 'lucide-react';
import { AutomationRule } from '../../types';

const GoogleAdsIcon = () => <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.84 3.18-1.74 4.1-.98.98-2.52 2.06-5.1 2.06-4.08 0-7.28-3.28-7.28-7.36s3.2-7.36 7.28-7.36c2.2 0 3.8.86 4.98 2l2.32-2.32C18.68 3.32 16.18 2 12.48 2 6.68 2 2 6.48 2 12s4.68 10 10.48 10c3.08 0 5.38-1.02 7.18-2.92 1.86-1.86 2.44-4.48 2.44-6.6 0-.66-.06-1.26-.16-1.76h-9.46z" fill="#4285F4"/></svg>;
const MetaAdsIcon = () => <img src="/logo_meta.png" alt="Meta" className="w-5 h-5" />;
const LinkedInAdsIcon = () => <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/></svg>;

interface AutomationRuleFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  editingRule: AutomationRule | null;
  connectedPlatforms: string[];
  initialData?: any;
}

const AutomationRuleForm: React.FC<AutomationRuleFormProps> = ({
  isOpen, onClose, onSubmit, editingRule, connectedPlatforms, initialData
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialData || {
    name: '', description: '',
    platform: connectedPlatforms.length > 0 ? connectedPlatforms : ['google', 'meta', 'linkedin'],
    trigger_type: 'ctr_below', trigger_value: 0,
    action_type: 'adjust_bid', action_value: 0,
    frequency: 'daily', status: 'active'
  });

  React.useEffect(() => {
    if (initialData) setFormData(initialData);
  }, [initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Trigger explanations for beginners
  const triggerExplanations: Record<string, string> = {
    ctr_below: t('automation.form.triggerHelp.ctrBelow', 'Dispara quando a taxa de cliques (CTR) ficar abaixo do valor definido. CTR baixo indica que o anúncio não está atraindo cliques.'),
    ctr_above: t('automation.form.triggerHelp.ctrAbove', 'Dispara quando a taxa de cliques (CTR) ultrapassar o valor definido. CTR alto indica anúncios com boa performance.'),
    cpc_above: t('automation.form.triggerHelp.cpcAbove', 'Dispara quando o custo por clique (CPC) ultrapassar o valor definido. CPC alto pode indicar lances muito agressivos.'),
    cpc_below: t('automation.form.triggerHelp.cpcBelow', 'Dispara quando o custo por clique (CPC) ficar abaixo do valor definido. CPC baixo pode ser oportunidade de escalar.'),
    spend_above: t('automation.form.triggerHelp.spendAbove', 'Dispara quando o gasto total ultrapassar o valor definido. Útil para proteger o orçamento contra gastos excessivos.'),
    roas_below: t('automation.form.triggerHelp.roasBelow', 'Dispara quando o retorno sobre gasto (ROAS) ficar abaixo do valor. ROAS baixo indica que o investimento não está gerando retorno.'),
    roas_above: t('automation.form.triggerHelp.roasAbove', 'Dispara quando o ROAS ultrapassar o valor definido. ROAS alto é oportunidade para aumentar investimento.'),
    cpa_above: t('automation.form.triggerHelp.cpaAbove', 'Dispara quando o custo por aquisição (CPA) ultrapassar o valor. CPA alto indica conversões muito caras.'),
    cpa_below: t('automation.form.triggerHelp.cpaBelow', 'Dispara quando o custo por aquisição (CPA) ficar abaixo do valor. CPA baixo é sinal de boa eficiência.'),
    spend_below: t('automation.form.triggerHelp.spendBelow', 'Dispara quando o gasto total ficar abaixo do valor definido. Pode indicar que a campanha não está entregando.'),
    impressions_below: t('automation.form.triggerHelp.impressionsBelow', 'Dispara quando as impressões ficarem abaixo do valor. Indica que a campanha não está alcançando público suficiente.'),
    clicks_below: t('automation.form.triggerHelp.clicksBelow', 'Dispara quando os cliques ficarem abaixo do valor. Indica que a campanha não está gerando tráfego suficiente.'),
  };

  // Action explanations
  const actionExplanations: Record<string, string> = {
    adjust_bid: t('automation.form.actionHelp.adjustBid', 'Ajusta o valor do lance automaticamente pela porcentagem definida. Valores positivos aumentam, negativos reduzem.'),
    pause_ad: t('automation.form.actionHelp.pauseAd', 'Pausa automaticamente o anúncio que disparou a condição. Útil para parar anúncios com baixo desempenho.'),
    enable_ad: t('automation.form.actionHelp.enableAd', 'Reativa automaticamente o anúncio. Útil para reativar anúncios quando condições melhoram.'),
    pause_campaign: t('automation.form.actionHelp.pauseCampaign', 'Pausa toda a campanha automaticamente. Útil para proteger o orçamento em caso de performance ruim.'),
    enable_campaign: t('automation.form.actionHelp.enableCampaign', 'Reativa toda a campanha automaticamente quando condições são favoráveis.'),
    send_notification: t('automation.form.actionHelp.sendNotification', 'Envia uma notificação para você sem alterar a campanha. Útil para monitorar sem agir automaticamente.'),
    adjust_budget: t('automation.form.actionHelp.adjustBudget', 'Ajusta o orçamento diário pela porcentagem definida. Valores positivos aumentam, negativos reduzem.'),
  };

  // Value prefix/suffix based on trigger/action type
  const getTriggerPrefix = () => {
    if (['spend_above', 'spend_below', 'cpc_above', 'cpc_below', 'cpa_above', 'cpa_below'].includes(formData.trigger_type)) return 'R$';
    return '';
  };

  const getTriggerSuffix = () => {
    if (['ctr_below', 'ctr_above'].includes(formData.trigger_type)) return '%';
    if (['roas_below', 'roas_above'].includes(formData.trigger_type)) return 'x';
    return '';
  };

  const getActionSuffix = () => {
    if (['adjust_bid', 'adjust_budget'].includes(formData.action_type)) return '%';
    return '';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {editingRule ? t('automation.form.editRule', 'Editar Regra') : t('automation.form.newRule', 'Nova Regra de Automação')}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
            <div className="md:col-span-2">
              <label className="label">{t('automation.form.ruleName', 'Nome da Regra')}</label>
              <input type="text" className="input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="md:col-span-2">
              <label className="label">{t('automation.form.description', 'Descrição')}</label>
              <textarea className="input" rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="label">{t('automation.form.platform', 'Plataforma')}</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mt-2">
                {[
                  { key: 'google', label: 'Google Ads', icon: <GoogleAdsIcon /> },
                  { key: 'meta', label: 'Meta Ads', icon: <MetaAdsIcon /> },
                  { key: 'linkedin', label: 'LinkedIn Ads', icon: <LinkedInAdsIcon /> }
                ].map((platform) => {
                  const isConnected = connectedPlatforms.includes(platform.key);
                  const isSelected = Array.isArray(formData.platform) ? formData.platform.includes(platform.key) : formData.platform === platform.key;
                  return (
                    <label key={platform.key} className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
                      !isConnected ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      : isSelected ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-500 text-primary-700 dark:text-primary-300 shadow-sm'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-primary-300'
                    }`}>
                      <input type="checkbox" checked={isSelected} disabled={!isConnected} onChange={(e) => {
                        if (!isConnected) return;
                        const current = Array.isArray(formData.platform) ? formData.platform : [formData.platform];
                        const newPlatforms = e.target.checked ? [...current, platform.key] : current.filter((p: string) => p !== platform.key);
                        if (newPlatforms.length > 0) setFormData({ ...formData, platform: newPlatforms });
                      }} className="sr-only" />
                      {platform.icon}
                      <span className="text-sm font-medium">{platform.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="label">{t('automation.form.frequency', 'Frequência de Verificação')}</label>
              <select className="input" value={formData.frequency} onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}>
                <option value="realtime">{t('automation.form.realtime', 'Tempo real')}</option>
                <option value="hourly">{t('automation.form.hourly', 'A cada hora')}</option>
                <option value="daily">{t('automation.form.daily', 'Diariamente')}</option>
                <option value="weekly">{t('automation.form.weekly', 'Semanalmente')}</option>
              </select>
            </div>
            <div>
              <label className="label">{t('automation.form.maxExecutions', 'Limite de Execuções')}</label>
              <input
                type="number"
                className="input"
                value={formData.max_executions || ''}
                onChange={(e) => setFormData({ ...formData, max_executions: e.target.value ? parseInt(e.target.value) : null })}
                placeholder={t('automation.form.maxExecutionsPlaceholder', 'Sem limite')}
                min="1"
              />
              <p className="text-xs text-gray-400 mt-1">
                {t('automation.form.maxExecutionsHelp', 'Quantidade máxima de vezes que a regra pode executar. Após atingir, será pausada automaticamente.')}
              </p>
            </div>
            {['adjust_bid', 'adjust_budget'].includes(formData.action_type) && (
              <div>
                <label className="label">{t('automation.form.maxCumulativeChange', 'Variação Máxima Acumulada (%)')}</label>
                <div className="relative">
                  <input
                    type="number"
                    className="input pr-9"
                    value={formData.max_cumulative_change || ''}
                    onChange={(e) => setFormData({ ...formData, max_cumulative_change: e.target.value ? parseFloat(e.target.value) : null })}
                    placeholder="100"
                    min="5"
                    max="500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {t('automation.form.maxCumulativeChangeHelp', 'Limite máximo de variação acumulada em relação ao valor original. Ex: 50% = lance/orçamento pode no máximo dobrar ou cair pela metade.')}
                </p>
              </div>
            )}
            <div className="md:col-span-2">
              <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary-500" />
                  {t('automation.form.conditionWhen', 'Condição (Quando)')}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label text-sm">{t('automation.form.metric', 'Métrica')}</label>
                    <select className="input" value={formData.trigger_type} onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}>
                      <option value="ctr_below">{t('automation.form.triggers.ctrBelow', 'CTR abaixo de')}</option>
                      <option value="ctr_above">{t('automation.form.triggers.ctrAbove', 'CTR acima de')}</option>
                      <option value="cpc_above">{t('automation.form.triggers.cpcAbove', 'CPC acima de')}</option>
                      <option value="cpc_below">{t('automation.form.triggers.cpcBelow', 'CPC abaixo de')}</option>
                      <option value="spend_above">{t('automation.form.triggers.spendAbove', 'Gasto acima de')}</option>
                      <option value="roas_below">{t('automation.form.triggers.roasBelow', 'ROAS abaixo de')}</option>
                      <option value="roas_above">{t('automation.form.triggers.roasAbove', 'ROAS acima de')}</option>
                      <option value="cpa_above">{t('automation.form.triggers.cpaAbove', 'CPA acima de')}</option>
                      <option value="cpa_below">{t('automation.form.triggers.cpaBelow', 'CPA abaixo de')}</option>
                      <option value="spend_below">{t('automation.form.triggers.spendBelow', 'Gasto abaixo de')}</option>
                      <option value="impressions_below">{t('automation.form.triggers.impressionsBelow', 'Impressões abaixo de')}</option>
                      <option value="clicks_below">{t('automation.form.triggers.clicksBelow', 'Cliques abaixo de')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="label text-sm">{t('automation.form.value', 'Valor')}</label>
                    <div className="relative">
                      {getTriggerPrefix() && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{getTriggerPrefix()}</span>
                      )}
                      <input
                        type="number"
                        className={`input ${getTriggerPrefix() ? 'pl-9' : ''} ${getTriggerSuffix() ? 'pr-9' : ''}`}
                        value={formData.trigger_value}
                        onChange={(e) => setFormData({ ...formData, trigger_value: parseFloat(e.target.value) })}
                        step="0.01"
                        required
                      />
                      {getTriggerSuffix() && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{getTriggerSuffix()}</span>
                      )}
                    </div>
                  </div>
                </div>
                {triggerExplanations[formData.trigger_type] && (
                  <p className="mt-3 text-xs text-gray-400 dark:text-gray-500 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    {triggerExplanations[formData.trigger_type]}
                  </p>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  {t('automation.form.actionThen', 'Ação (Então)')}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label text-sm">{t('automation.form.actionType', 'Tipo de Ação')}</label>
                    <select className="input" value={formData.action_type} onChange={(e) => setFormData({ ...formData, action_type: e.target.value })}>
                      <option value="adjust_bid">{t('automation.form.actions.adjustBid', 'Ajustar lance')}</option>
                      <option value="pause_ad">{t('automation.form.actions.pauseAd', 'Pausar anúncio')}</option>
                      <option value="enable_ad">{t('automation.form.actions.enableAd', 'Ativar anúncio')}</option>
                      <option value="pause_campaign">{t('automation.form.actions.pauseCampaign', 'Pausar campanha')}</option>
                      <option value="enable_campaign">{t('automation.form.actions.enableCampaign', 'Ativar campanha')}</option>
                      <option value="send_notification">{t('automation.form.actions.sendNotification', 'Enviar notificação')}</option>
                      <option value="adjust_budget">{t('automation.form.actions.adjustBudget', 'Ajustar orçamento')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="label text-sm">{t('automation.form.actionValue', 'Valor')}</label>
                    <div className="relative">
                      <input
                        type="number"
                        className={`input ${getActionSuffix() ? 'pr-9' : ''}`}
                        value={formData.action_value}
                        onChange={(e) => setFormData({ ...formData, action_value: parseFloat(e.target.value) })}
                        step="1"
                        disabled={['pause_ad', 'enable_ad', 'pause_campaign', 'enable_campaign', 'send_notification'].includes(formData.action_type)}
                      />
                      {getActionSuffix() && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{getActionSuffix()}</span>
                      )}
                    </div>
                  </div>
                </div>
                {actionExplanations[formData.action_type] && (
                  <p className="mt-3 text-xs text-gray-400 dark:text-gray-500 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    {actionExplanations[formData.action_type]}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary">{t('common.cancel', 'Cancelar')}</button>
            <button type="submit" className="btn-primary">{editingRule ? t('automation.form.saveChanges', 'Salvar Alterações') : t('automation.form.create', 'Criar Regra')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AutomationRuleForm;
